import { Network, PuzzleAccount } from '@puzzlehq/sdk';
import { trpc, trpcReact } from '@src/data/trpc';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useArcadeStoreState } from './useArcadeStoreState';
import { extractAmountFromInputString, RaffleV2 } from 'jigsaw';
import { useQuery } from '@tanstack/react-query';
import { getNetworkUrl } from '@src/data/utils';
import { useAuth } from '@src/data/settings';

const network: Network = import.meta.env.VITE_NETWORK!;

export const useCurrentGiveawaysQuery = () => {
  const { countryCode } = useArcadeStoreState();
  return trpcReact.getCurrentRafflesV2.useQuery({
    network,
    userCountry: countryCode,
  });
};

export const usePastGiveawaysQuery = () => {
  return trpcReact.getPastRafflesV2.useQuery({ network });
};

export const useUserCurrentGiveawaysQuery = () => {
  const { account } = useArcadeStoreState();
  return trpcReact.getUserCurrentRafflesV2.useQuery(
    {
      network,
      address: account?.address!,
    },
    { enabled: !!account }
  );
};

export const useUserPastGiveawaysQuery = () => {
  const { account } = useArcadeStoreState();
  return trpcReact.getUserPastRafflesV2.useQuery(
    {
      network,
      address: account?.address!,
    },
    { enabled: !!account }
  );
};

export const useAllGiveawaysQuery = () => {
  const { countryCode } = useArcadeStoreState();
  return trpcReact.getRafflesV2.useQuery({ network, userCountry: countryCode });
};

export const useGiveawayTotalEntriesQuery = (giveaway?: RaffleV2) => {
  const totalGiveawayEntriesQuery = useQuery<number>({
    enabled: !!giveaway,
    queryKey: [giveaway?.programId, giveaway?.giveawayId],
    queryFn: async () => {
      const mappingKey = giveaway!.giveawayId ?? '0u64';
      const res = await fetch(
        `${getNetworkUrl(network ?? Network.AleoMainnet)}/program/${
          giveaway!.programId
        }/mapping/total_entries/${mappingKey}`
      );
      let text = await res.text();
      text = text === 'null' ? '0u64' : text?.replace(/"/g, '') ?? '0u64';
      const value = extractAmountFromInputString(text);
      return value ?? 0;
    },
    refetchInterval: 1_000 * 60,
  });

  return totalGiveawayEntriesQuery;
};

export const useGiveawayUserEntriesQuery = (giveaway: RaffleV2) => {
  const { account } = useArcadeStoreState();

  const userGiveawayEntriesQuery = useQuery<number>({
    queryKey: [giveaway.programId, giveaway.giveawayId, account?.address],
    queryFn: async () => {
      if (!!giveaway.giveawayId) {
        const userEntries = await trpc.getUserEntries.query({
          giveawayId: giveaway.giveawayId,
          address: account!.address,
          network: network,
        });
        return userEntries;
      } else {
        // old method
        const res = await fetch(
          `${getNetworkUrl(network ?? Network.AleoMainnet)}/program/${
            giveaway.programId
          }/mapping/user_entries/${account?.address}`
        );
        let text = await res.text();
        text = text === 'null' ? '0u64' : text?.replace(/"/g, '') ?? '0u64';
        const value = extractAmountFromInputString(text);
        return value ?? 0;
      }
    },
    enabled: !!account,
    refetchInterval: 1_000 * 60,
  });

  return userGiveawayEntriesQuery;
};

export const useGiveawayStats = (giveaway: RaffleV2) => {
  const { account } = useArcadeStoreState();
  const totalGiveawayEntriesQuery = useGiveawayTotalEntriesQuery(giveaway);
  const userGiveawayEntriesQuery = useGiveawayUserEntriesQuery(giveaway);

  const { data } = trpcReact.getUserPendingGiveawayEntries.useQuery(
    {
      programId: giveaway.programId,
      address: account?.address!,
      network: network,
      giveawayId: giveaway.giveawayId,
    },
    {
      enabled: !!account,
    }
  );

  return {
    totalGiveawayEntriesQuery,
    userGiveawayEntriesQuery,
    hasPendingEntries: data?.hasPendingEntries,
  };
};

export const getIsGiveawayClosed = (giveaway: RaffleV2) => {
  const now = new Date();
  return giveaway.cutoff < now;
};

export const getIsGiveawayOver = (giveaway: RaffleV2) => {
  const now = new Date();
  return giveaway.draw ? giveaway.draw < now : false;
};

export const getIsGiveawayWinner = (
  giveaway: RaffleV2,
  account: PuzzleAccount
) => {
  if (giveaway.winnerAddresses.length > 0) {
    return giveaway.winnerAddresses.includes(account.address);
  }
  return false;
};

export const useGiveawayUtils = (giveaway?: RaffleV2) => {
  const { account } = useArcadeStoreState();
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const intervalId = setInterval(() => {
      setNow(new Date());
    }, 500);
    return () => clearInterval(intervalId); // Cleanup the interval on component unmount
  }, []);

  const { data: totalEntries } = useGiveawayTotalEntriesQuery(giveaway);

  const {
    isGiveawayClosed,
    isGiveawayOver,
    isGiveawayWinner,
    isGiveawayFull,
    remainingEntries,
  } = useMemo(() => {
    if (!giveaway) {
      return {
        isGiveawayClosed: undefined,
        isGiveawayOver: undefined,
        isGiveawayWinner: undefined,
      };
    }

    const isGiveawayFull =
      !!giveaway.maxEntries && (totalEntries ?? 0) >= giveaway.maxEntries;

    const remainingEntries = !!giveaway.maxEntries
      ? giveaway.maxEntries - (totalEntries ?? 0)
      : undefined;

    return {
      isGiveawayFull,
      isGiveawayClosed: getIsGiveawayClosed(giveaway),
      isGiveawayOver: getIsGiveawayOver(giveaway),
      isGiveawayWinner: account
        ? getIsGiveawayWinner(giveaway, account)
        : undefined,
      remainingEntries,
    };
  }, [account, JSON.stringify(giveaway), Math.round(now.getTime() / 1_000)]);

  return {
    isGiveawayClosed,
    isGiveawayOver,
    isGiveawayWinner,
    isGiveawayFull,
    remainingEntries,
  };
};

export const useGiveawayPrize = (giveaway: RaffleV2) => {
  const { account } = useArcadeStoreState();
  const [auth, challengeAuth] = useAuth((state) => [
    state.auth,
    state.challengeAuth,
  ]);

  return trpcReact.getGiveawayPrize.useQuery(
    {
      address: account?.address!,
      auth: auth,
      challengeAuth: challengeAuth,
      giveawayId: (giveaway.giveawayId ?? giveaway.programId)!,
      network: network,
    },
    {
      enabled:
        !!account &&
        (!!auth || !!challengeAuth) &&
        !!(giveaway.giveawayId ?? giveaway.programId),
    }
  );
};
